// The ConditionalFormField children below are load-bearing: FormFields returns null for
// relationship fields, so deleting the PERSON child silently drops it, no build error.
import React, { ComponentType, ReactNode } from "react";

import { faPhone } from "@fortawesome/pro-solid-svg-icons/faPhone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { EntityType, Person } from "@mapmycustomers/shared/types/entity";
import { RawFile } from "@mapmycustomers/shared/types/File";
import FormLayout, { LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import { FormItem, NumberField, TextField } from "@mapmycustomers/ui";

import ConditionalFormField, {
  ConditionalFormFieldRenderProps,
} from "@app/component/ConditionalFormField";
import FormFields from "@app/component/FormFields";
import PersonRelationshipField from "@app/component/FormFields/components/Associations/PersonRelationshipField";
import { CompanyFieldName } from "@app/util/fieldModel/CompanyFieldModel";

import File from "./components/File";

export interface CompanyFormFieldsProps {
  defaultAddedFields?: LayoutSchemaField[];
  fileComponent?: ComponentType<{
    disabled?: boolean;
    onChange?: (uploadedFiles: RawFile[]) => void;
    required?: boolean;
    supportsTags?: boolean;
  }>;
  fixedPerson?: Person;
  layout: FormLayout;
  onAddField?: (schemaField: LayoutSchemaField) => void;
  previewWarning?: ReactNode;
  skipLayoutDefaultsFor?: Set<LayoutSchemaField["field"]>;
}

const CompanyFormFields = ({
  defaultAddedFields,
  fileComponent,
  fixedPerson,
  layout,
  onAddField,
  previewWarning,
  skipLayoutDefaultsFor,
}: CompanyFormFieldsProps) => {
  return (
    <>
      {previewWarning}
      <FormItem hidden name="sourceObject" />
      <FormFields
        defaultAddedFields={defaultAddedFields}
        entityType={EntityType.COMPANY}
        fileComponent={fileComponent ?? File}
        isCreateForm
        layout={layout}
        onAddField={onAddField}
        skipLayoutDefaultsFor={skipLayoutDefaultsFor}
      >
        <ConditionalFormField fieldName={CompanyFieldName.PHONE}>
          {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
            <FormItem
              label={label}
              name={CompanyFieldName.PHONE}
              required={required}
              rules={[{ required }]}
              showLabel={false}
            >
              <TextField
                addonBefore={<FontAwesomeIcon icon={faPhone} />}
                disabled={disabled}
                label={label}
                placeholder={label?.toString()}
                required={required}
              />
            </FormItem>
          )}
        </ConditionalFormField>

        <ConditionalFormField fieldName={CompanyFieldName.NUM_EMPLOYEES}>
          {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
            <FormItem
              label={label}
              name={CompanyFieldName.NUM_EMPLOYEES}
              required={required}
              rules={[{ required }]}
              showLabel={false}
            >
              <NumberField
                disabled={disabled}
                label={label}
                min={1}
                placeholder={typeof label === "string" ? label : undefined}
                required={required}
              />
            </FormItem>
          )}
        </ConditionalFormField>

        <ConditionalFormField fieldName={CompanyFieldName.PERSON}>
          {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
            <PersonRelationshipField
              allowAdd={!disabled}
              allowClear
              associatedPeople={fixedPerson ? [fixedPerson] : undefined}
              disabled={disabled || !!fixedPerson}
              label={label}
              required={required}
            />
          )}
        </ConditionalFormField>
      </FormFields>
    </>
  );
};

export default CompanyFormFields;
